import {
  CourseEntity,
  Enum_Favorite_Type,
  FavoriteEntityResponseCollection,
  FavouritePage,
  PodcastEntity,
  ResourceEntity,
} from '@/graphql/generated/graphql';
import { fetchFavouritePage, fetchUserFavorites } from '@/services/favorite/favorite.service';
import { create } from 'zustand';

const SIZE = 3;
interface FavoriteStoreProps {
  podcastsFavorites: FavoriteEntityResponseCollection | null;
  podcasts: PodcastEntity[];
  podcastsSize: number;
  showPodcastsMoreButton: boolean;

  resourcesFavorites: FavoriteEntityResponseCollection | null;
  resources: ResourceEntity[];
  resourcesSize: number;
  showResourcesMoreButton: boolean;

  coursesFavorites: FavoriteEntityResponseCollection | null;
  courses: CourseEntity[];
  coursesSize: number;
  showCoursesMoreButton: boolean;

  page: FavouritePage | null;

  fetchFavorites: (type: Enum_Favorite_Type, size: number) => Promise<void>;
  removeFromFavourite: (type: Enum_Favorite_Type) => Promise<void>;
  onInit: () => Promise<void>;
  onInitPage: () => Promise<void>;

  handleMore: (type: Enum_Favorite_Type) => void;
  fetchAndSetPodcasts: () => Promise<void>;
  fetchAndSetResources: () => Promise<void>;
  fetchAndSetCourses: () => Promise<void>;
}

export const useFavoriteStore = create<FavoriteStoreProps>()((set, get) => ({
  podcasts: [],
  podcastsFavorites: null,
  podcastsSize: SIZE,
  showPodcastsMoreButton: false,

  resources: [],
  resourcesFavorites: null,
  resourcesSize: SIZE,
  showResourcesMoreButton: false,

  courses: [],
  coursesFavorites: null,
  coursesSize: SIZE,
  showCoursesMoreButton: false,

  page: null,

  onInit: async () => {
    const { podcastsSize, resourcesSize, coursesSize, fetchFavorites, onInitPage } = get();
    await Promise.all([
      fetchFavorites(Enum_Favorite_Type.Podcast, podcastsSize),
      fetchFavorites(Enum_Favorite_Type.Resource, resourcesSize),
      fetchFavorites(Enum_Favorite_Type.Learn, coursesSize),
      onInitPage(),
    ]);
  },
  onInitPage: async () => {
    const pageResponse = await fetchFavouritePage();
    set({ page: pageResponse.data?.favouritePage?.data?.attributes as FavouritePage });
  },
  fetchFavorites: async (type: Enum_Favorite_Type, size: number) => {
    const favoritesResponse = await fetchUserFavorites(type, size);

    if (type === Enum_Favorite_Type.Podcast) {
      set({
        podcastsFavorites: favoritesResponse.data?.favorites as FavoriteEntityResponseCollection,
        podcastsSize: size,
      });
      get().fetchAndSetPodcasts();
    }

    if (type === Enum_Favorite_Type.Resource) {
      set({
        resourcesFavorites: favoritesResponse.data?.favorites as FavoriteEntityResponseCollection,
        resourcesSize: size,
      });
      get().fetchAndSetResources();
    }

    if (type === Enum_Favorite_Type.Learn) {
      set({
        coursesFavorites: favoritesResponse.data?.favorites as FavoriteEntityResponseCollection,
        coursesSize: size,
      });
      get().fetchAndSetCourses();
    }
  },
  removeFromFavourite: async (type: Enum_Favorite_Type) => {
    await get().fetchFavorites(type, SIZE);
  },
  fetchAndSetPodcasts: async () => {
    const { podcastsFavorites, podcastsSize } = get();

    const podcasts = podcastsFavorites?.data.slice(0, podcastsSize);
    if (podcasts?.length === 0) return set({ podcastsFavorites: null, podcasts: [] });

    set({
      podcasts: podcasts?.map((podcast) => JSON.parse(podcast.attributes?.metadata || '{}') as PodcastEntity),
      showPodcastsMoreButton: (podcastsFavorites?.meta?.pagination.total || 0) > podcastsSize,
    });
  },
  fetchAndSetResources: async () => {
    const { resourcesFavorites, resourcesSize } = get();

    const resources = resourcesFavorites?.data.slice(0, resourcesSize);
    if (resources?.length === 0) return set({ resourcesFavorites: null, resources: [] });

    set({
      resources: resources?.map((resource) => JSON.parse(resource.attributes?.metadata || '{}') as ResourceEntity),
      showResourcesMoreButton: (resourcesFavorites?.meta?.pagination.total || 0) > resourcesSize,
    });
  },
  fetchAndSetCourses: async () => {
    const { coursesFavorites, coursesSize } = get();

    const courses = coursesFavorites?.data.slice(0, coursesSize);
    if (courses?.length === 0) return set({ coursesFavorites: null, courses: [] });

    set({
      courses: courses?.map((course) => JSON.parse(course.attributes?.metadata || '{}') as CourseEntity),
      showCoursesMoreButton: (coursesFavorites?.meta?.pagination.total || 0) > coursesSize,
    });
  },

  handleMore: async (type: Enum_Favorite_Type) => {
    const size = type === Enum_Favorite_Type.Podcast ? get().podcastsSize + SIZE : get().resourcesSize + SIZE;
    await get().fetchFavorites(type, size);
  },
}));
